import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { Brain, Copy, Check, Clock, Hash, ChevronUp, ChevronDown, AlertTriangle, ShieldAlert } from "lucide-react";
import { useFontSize } from "@/hooks/use-font-size";
import React, { useState, useEffect, useRef, memo } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useHapticFeedback } from "@/hooks/use-haptic";
import { Message, ModelType } from "@/types/chat";
import { AudioPlayerDialog } from "./audio-player-dialog";
import { detectLanguage, getFontFamilyForText, isArabicText } from "@/lib/language-detector";
import { useTranslation } from "@/lib/translation-utils";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
// Import the global cache initializer for audio player support
import { initializeCache } from "@/lib/cache-initializer";

// Initialize cache once at module load
initializeCache();

// Optimized ThoughtProcessBubble with performance enhancements
const ThoughtProcessBubble = memo(function ThoughtProcessBubble({ 
  content, 
  isRTL, 
  fontSize, 
  expanded, 
  onToggle 
}: {
  content: string;
  isRTL: boolean;
  fontSize: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  // Use the memoized getFontFamily function
  const fontFamily = getFontFamily(isRTL ? "AI Reasoning" : "AI Reasoning");
  const contentFontFamily = getFontFamily(content);
  
  return (
    <motion.div
      className={cn(
        "rounded-xl message-bubble border border-primary/30 bg-background shadow-lg relative flex-1 mb-2 will-change-transform",
        fontSize < 14 ? "thought-bubble-padding-small" : "thought-bubble-padding-large",
        isRTL ? "text-right" : "text-left"
      )}
      style={{
        direction: isRTL ? 'rtl' : 'ltr',
        contain: 'content',
        willChange: 'transform'
      }}
      initial={false}
      layoutId={`thought-bubble-${expanded}`}
    >
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between py-1.5 transition-colors outline-none cursor-pointer rounded-xl text-foreground/90"
        aria-expanded={expanded}
        aria-controls="thought-process-content"
      >
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" />
          <span 
            className="font-semibold text-primary"
            style={{ 
              fontSize: `${fontSize}px`,
              fontFamily
            }}
          >
            AI Reasoning
          </span>
        </div>
        <motion.div 
          initial={false}
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-4 w-4 text-primary" />
        </motion.div>
      </button>

      <motion.div
        id="thought-process-content"
        initial={false}
        animate={{ 
          height: expanded ? 'auto' : 0,
          opacity: expanded ? 1 : 0
        }}
        transition={{ 
          duration: 0.2,
          ease: "easeInOut"
        }}
        className="overflow-hidden"
        style={{ 
          willChange: 'height, opacity',
          transform: 'translateZ(0)'
        }}
      >
        <div 
          className="text-xs sm:text-sm lg:text-base message-text whitespace-pre-wrap mt-4 ai-reasoning-text"
          style={{ 
            fontFamily: contentFontFamily,
            fontSize: `${fontSize}px`,
          }}
        >
          {content}
        </div>
      </motion.div>
    </motion.div>
  );
});

export function MessageBubble({ message, isLoading, isPartial, model, children }: MessageBubbleProps) {
  const isUser = message.role === "user";
  const { fontSize, screenSize } = useFontSize();
  const { triggerHaptic } = useHapticFeedback();
  const { t } = useTranslation(); // Add useTranslation hook to make component re-render on language change
  const [copied, setCopied] = useState(false);
  const [isRTL, setIsRTL] = useState(false);
  const [isAudioPlayerOpen, setIsAudioPlayerOpen] = useState(false);
  const [thoughtExpanded, setThoughtExpanded] = useState(false);
  const messageBubbleRef = useRef<HTMLDivElement>(null);

  // Extract thought process from content
  const thoughtMatch = message.content.match(/<think>([\s\S]*?)<\/think>/);
  const thoughtContent = thoughtMatch ? thoughtMatch[1].trim() : null;
  const mainContent = message.content.replace(/<think>[\s\S]*?<\/think>/g, '').trim();

  useEffect(() => {
    setIsRTL(isArabicText(message.content));
  }, [message.content]);

  const avatarSize = screenSize === 'desktop' ? 'w-12 h-12' : screenSize === 'tablet' ? 'w-10 h-10' : 'w-8 h-8';
  const padding = screenSize === 'desktop' ? 'px-6 py-4' : screenSize === 'tablet' ? 'px-5 py-3' : 'px-3 py-2';

  const formatTimestamp = (date: string | number | Date) => {
    return new Intl.DateTimeFormat('default', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(date));
  };

  const timestamp = formatTimestamp(message.timestamp);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      await triggerHaptic('success');
      setCopied(true);
      toast({ description: t("Message copied to clipboard"), duration: 2000 });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('[MessageBubble] Copy failed:', error);
      toast({
        description: t("Failed to copy message"),
        variant: "destructive",
        duration: 2000,
      });
    }
  };

  const handleMicClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('[MessageBubble] Summarize button clicked');
    try {
      await triggerHaptic("selection");
      setIsAudioPlayerOpen(true);
    } catch (error) {
      console.warn('[MessageBubble] Haptic failed:', error);
      setIsAudioPlayerOpen(true);
    }
    
    // Make sure audio dialog opens even if there are issues
    setTimeout(() => {
      if (!isAudioPlayerOpen) {
        console.log('[MessageBubble] Force opening audio player dialog');
        setIsAudioPlayerOpen(true);
      }
    }, 100);
  };

  // Optimized scroll handler with proper cleanup
  const handleScrollToMessage = async () => {
    try {
      // First trigger haptic feedback
      await triggerHaptic('selection');
      
      // Check if references exist before proceeding
      if (!messageBubbleRef.current) {
        console.warn('[MessageBubble] Cannot scroll - message ref is null');
        return;
      }
      
      const chatContainer = document.querySelector('.chat-main');
      if (!chatContainer) {
        console.warn('[MessageBubble] Cannot scroll - chat container not found');
        return;
      }
      
      // Calculate position outside of requestAnimationFrame for better performance
      // as DOM reads should be batched together
      const messagePosition = messageBubbleRef.current.offsetTop;
      
      // Use a tracked rAF ID so we can potentially cancel it
      let rafId: number;
      
      // Schedule the actual scrolling in the next frame
      rafId = requestAnimationFrame(() => {
        chatContainer.scrollTo({
          top: messagePosition - 100,
          behavior: 'smooth'
        });
      });
      
      // Return cleanup function that will be discarded in this case,
      // but follows the pattern for consistent code style
      return () => cancelAnimationFrame(rafId);
    } catch (error) {
      console.warn('[MessageBubble] Scroll failed:', error);
    }
  };

  // Detect if this is a system introduction or disclaimer message that shouldn't have controls
  const isSystemMessage = message.id === 'welcome' || message.id === 'disclaimer' || 
    message.content.includes("Welcome to") ||
    message.content.includes("Disclaimer:") ||
    message.content.includes("I'm Mila, your AI assistant") ||
    message.content.includes("Hello, I am Mila, an AI");

  const shouldShowControls = !isUser && (!isPartial || (model && model !== 'claude')) && !isLoading && !isSystemMessage;

  // Localized text for the system notice banner (truncation / safety refusal)
  const noticeText = message.notice === 'refusal'
    ? t("This request can't be answered due to safety guidelines. Please rephrase or ask about a different topic.")
    : message.notice === 'truncated'
      ? t("This answer was shortened because it reached the length limit and may be incomplete.")
      : '';
  // Derive the notice banner direction from the localized notice text itself,
  // since refusal notices have empty message.content (which would force LTR).
  const noticeIsRTL = noticeText ? isArabicText(noticeText) : isRTL;

  return (
    <div
      className={cn(
        "space-y-1 group hover:bg-muted/5 rounded-xl transition-colors duration-200 w-full",
        isUser ? "items-end" : "items-start"
      )}
      ref={messageBubbleRef}
    >
      <div className={cn(
        "flex items-start gap-2 sm:gap-3 lg:gap-4 relative w-full",
        isUser ? "flex-row-reverse" : "flex-row"
      )}>
        {!isUser && (
          <div className="flex flex-col items-center gap-2">
            <motion.div
              className={cn(avatarSize, "rounded-full overflow-hidden")}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <img
                // Use properly optimized srcSet with explicit widths for better browser optimization
                srcSet={`${message.avatarSrc || "/assets/icons/Mila-Avatar.png"}`}
                // Precise size instructions help browser select the right image
                sizes="(max-width: 640px) 32px, (max-width: 768px) 40px, 48px"
                // Fallback image for browsers that don't support srcSet
                src={message.avatarSrc || "/assets/icons/Mila-Avatar.png"}
                alt="Mila Saudi Labor Law Expert"
                className="w-full h-full object-cover avatar-image"
                // Prioritize welcome message loading
                loading={message.id === 'welcome' ? 'eager' : 'lazy'}
                // Add decoding attribute to allow asynchronous decoding
                decoding="async"
                // Add width and height to prevent layout shifts (CLS)
                width={screenSize === 'desktop' ? 48 : screenSize === 'tablet' ? 40 : 32}
                height={screenSize === 'desktop' ? 48 : screenSize === 'tablet' ? 40 : 32}
              />
            </motion.div>
          </div>
        )}

        <div className="flex-1 flex flex-col">
          {!isUser && thoughtContent && (
            <ThoughtProcessBubble 
              content={thoughtContent}
              isRTL={isRTL}
              fontSize={fontSize}
              expanded={thoughtExpanded}
              onToggle={() => setThoughtExpanded(!thoughtExpanded)}
            />
          )}

          <motion.div
            className={cn(
              "rounded-xl message-bubble border border-primary/10",
              padding,
              isUser
                ? "bg-primary/10 text-foreground backdrop-blur-sm"
                : "text-foreground",
              isLoading && !message.content ? "loading-animation" : isPartial ? "typing-animation" : "",
              "relative flex-1"
            )}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              fontSize: `${fontSize}px`,
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            role="article"
            aria-label={`${isUser ? 'Your' : "Assistant's"} message: ${mainContent ? mainContent.substring(0, 50) + '...' : 'Loading...'}`}
          >
            <div className="relative min-h-[24px]">
              {/* Loading animation - fades out when content arrives */}
              {isLoading && (
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ opacity: 1 }}
                  animate={{ 
                    opacity: message.content ? 0 : 1,
                    scale: message.content ? 0.8 : 1
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {children}
                </motion.div>
              )}
              
              {/* Message content - fades in when content arrives */}
              <motion.div
                className={cn(
                  "message-text",
                  isRTL ? "text-right" : "text-left",
                  mainContent ? "block" : "invisible"
                )}
                style={{
                  textAlign: isRTL ? 'right' : 'left'
                }}
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: mainContent ? 1 : 0,
                  y: mainContent ? 0 : 10
                }}
                transition={{ duration: 0.3 }}
              >
                <MarkdownContent content={mainContent} fontSize={fontSize} isRTL={isRTL} />
              </motion.div>

              {/* System notice banner (truncation / safety refusal) */}
              {message.notice && noticeText && (
                <div
                  className={cn(
                    "flex items-start gap-2 mt-2 px-3 py-2 rounded-xl border text-sm",
                    message.notice === 'refusal'
                      ? "border-destructive/30 bg-destructive/10 text-destructive"
                      : "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                  )}
                  style={{ direction: noticeIsRTL ? 'rtl' : 'ltr', textAlign: noticeIsRTL ? 'right' : 'left' }}
                  role="status"
                >
                  {message.notice === 'refusal'
                    ? <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                    : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />}
                  <span style={{ fontFamily: getFontFamily(noticeText) }}>{noticeText}</span>
                </div>
              )}
            </div>

            {/* Message controls */}
            {(shouldShowControls || isUser) && (
              <div className="message-controls-row">
                {/* Left side: Timestamp and copy button */}
                <div className={cn(
                  "controls-left",
                  isRTL ? "rtl" : "ltr"
                )}>
                  <div className="timestamp">
                    <Clock className="timestamp-icon" aria-hidden="true" />
                    <span className="timestamp-text">{timestamp}</span>
                  </div>

                  <div className="controls-divider" aria-hidden="true" />

                  <button
                    className="copy-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy();
                    }}
                    aria-label={`Copy ${isUser ? 'your' : "assistant's"} message`}
                  >
                    {copied ? (
                      <Check className="btn-icon" aria-hidden="true" />
                    ) : (
                      <Copy className="btn-icon" aria-hidden="true" />
                    )}
                  </button>
                </div>

                {/* Right side: Summarize and Read buttons */}
                {shouldShowControls && (
                  <div className={cn(
                    "controls-right",
                    isRTL ? "rtl" : "ltr"
                  )}>
                    {isRTL ? (
                      // For RTL languages (Arabic), we swap the button order
                      <>
                        <button 
                          className="control-btn read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScrollToMessage();
                          }}
                          aria-label={t("Read message")}
                        >
                          <span className="btn-text">{t("Read")}</span>
                          <ChevronUp className="btn-icon" aria-hidden="true" />
                        </button>
                        
                        <button 
                          className="control-btn summarize-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMicClick(e);
                          }}
                          aria-label={t("Summarize message")}
                        >
                          <span className="summarize-emoji">📝</span>
                          <span className="btn-text">{t("Summarize")}</span>
                        </button>
                      </>
                    ) : (
                      // For LTR languages (English, etc.), keep original order
                      <>
                        <button 
                          className="control-btn summarize-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMicClick(e);
                          }}
                          aria-label={t("Summarize message")}
                        >
                          <span className="summarize-emoji">📝</span>
                          <span className="btn-text">{t("Summarize")}</span>
                        </button>
                        
                        <button 
                          className="control-btn read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleScrollToMessage();
                          }}
                          aria-label={t("Read message")}
                        >
                          <span className="btn-text">{t("Read")}</span>
                          <ChevronUp className="btn-icon" aria-hidden="true" />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>

        {isUser && (
          <div className="flex flex-col items-center gap-2">
            <div className={avatarSize} />
          </div>
        )}
      </div>
      <AudioPlayerDialog
        isOpen={isAudioPlayerOpen}
        onClose={() => setIsAudioPlayerOpen(false)}
        message={message}
      />
    </div>
  );
}

// Memoized getFontFamily function with a simple cache
const fontFamilyCache = new Map<string, string>();
const getFontFamily = (text: string | undefined): string => {
  // Return empty string for undefined or empty text
  if (!text) return '';
  
  // Use cached result if available
  if (fontFamilyCache.has(text)) {
    return fontFamilyCache.get(text)!;
  }
  
  // Calculate and cache result
  const result = getFontFamilyForText(text);
  fontFamilyCache.set(text, result);
  return result;
};

// Renders assistant/user message content as full GitHub-flavored Markdown
// (headings, lists, tables, links, code, blockquotes) while preserving the
// app's RTL handling and per-message font selection. Replaces the previous
// hand-rolled parser that only supported bold, headers and single-line quotes.
const MarkdownContent = memo(function MarkdownContent({
  content,
  fontSize,
  isRTL,
}: {
  content: string;
  fontSize: number;
  isRTL: boolean;
}) {
  const fontFamily = getFontFamily(content);
  return (
    <div
      className="markdown-content message-text break-words [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-foreground"
      style={{ fontFamily, fontSize: `${fontSize}px`, direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <div className="flex items-center gap-2 font-semibold text-primary my-2 text-lg sm:text-xl">
              <Hash className="h-4 w-4 shrink-0" />
              <span>{children}</span>
            </div>
          ),
          h2: ({ children }) => (
            <div className="flex items-center gap-2 font-semibold text-primary my-2 text-base sm:text-lg">
              <Hash className="h-4 w-4 shrink-0" />
              <span>{children}</span>
            </div>
          ),
          h3: ({ children }) => (
            <div className="font-semibold text-primary my-1.5 text-base">{children}</div>
          ),
          h4: ({ children }) => (
            <div className="font-semibold text-primary my-1.5">{children}</div>
          ),
          p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
          strong: ({ children }) => <strong className="text-primary font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc ps-5 my-2 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal ps-5 my-2 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="ps-4 pe-3 py-2 my-2 border-s-4 border-primary/40 bg-primary/10 rounded-xl text-foreground/90 shadow-sm">
              {children}
            </blockquote>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 break-words"
            >
              {children}
            </a>
          ),
          code: ({ className, children, ...props }) => {
            const isBlock = (className && /language-/.test(className)) || String(children).includes('\n');
            if (isBlock) {
              return (
                <code className={cn("font-mono text-[0.9em]", className)} {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className="px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono text-[0.9em]" {...props}>
                {children}
              </code>
            );
          },
          pre: ({ children }) => (
            <pre className="my-2 p-3 rounded-xl bg-muted overflow-x-auto text-[0.9em] leading-relaxed" dir="ltr">
              {children}
            </pre>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto my-3">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-primary/10">{children}</thead>,
          th: ({ children }) => (
            <th className="border border-primary/20 px-3 py-1.5 text-start font-semibold">{children}</th>
          ),
          td: ({ children }) => (
            <td className="border border-primary/20 px-3 py-1.5 align-top">{children}</td>
          ),
          hr: () => <hr className="my-3 border-primary/20" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
});

type MessageBubbleProps = {
  message: Message;
  isLoading?: boolean;
  isPartial?: boolean;
  model?: ModelType;
  children?: React.ReactNode;
};